import { ComplexEnumItem } from '../../utils/complexEnum/ComplexEnumItem'
import { CIMSModuleItem } from '../CIMSModule/CIMSModuleItem'
import {
  CAS,
  Concrete,
  DS,
  FDC,
  RFI,
  Issues as Is,
} from '../CIMSModule/instances'

/**
 * this is not extending DetailedEnumItem on purpose
 */
export class SharePointParentFolderItem<
  ID extends Lowercase<string>,
  Name extends string,
  ModuleID extends Lowercase<string>,
  ModuleName extends string,
  ModulePathname extends string,
  ClassType extends string,
  PrimaryKey extends string,
  FolderKey extends string
> extends ComplexEnumItem<ID> {
  readonly classType: ClassType
  /**
   * the name of the parent folder in SharePoint
   *
   * this is where all the various record folders live
   */
  readonly name: Name
  /**
   * which CIMS module the record's {@link SharePointParentFolderItem.description|parent}
   * is associated with
   */
  readonly module: CIMSModuleItem<ModuleID, ModuleName, ModulePathname>
  /**
   * text describing how the folder for a single record is generated
   *
   * this property is **not** the same as {@link SharePointFolderReference.folder}
   */
  readonly description: string
  readonly primaryKey: PrimaryKey
  /**
   * the key of {@link ClassType} that references the folder for any given record
   */
  readonly folderKey: FolderKey
  constructor({
    id,
    name,
    description,
    module,
    classType,
    primaryKey,
    folderKey,
  }: Pick<
    SharePointParentFolderItem<
      ID,
      Name,
      ModuleID,
      ModuleName,
      ModulePathname,
      ClassType,
      PrimaryKey,
      FolderKey
    >,
    | 'id'
    | 'name'
    | 'description'
    | 'module'
    | 'classType'
    | 'primaryKey'
    | 'folderKey'
  >) {
    super(id)
    this.name = name
    this.description = description
    this.module = module
    this.classType = classType
    this.primaryKey = primaryKey
    this.folderKey = folderKey
  }
}

export const ConcretePackets = new SharePointParentFolderItem({
  id: 'a775b3e2-a784-4aa0-9c14-b438710876e3',
  module: Concrete,
  name: 'Concrete Packets',
  description: 'Test Set ID',
  classType: 'ConcreteTestSet',
  primaryKey: 'globalId',
  folderKey: 'testSetId',
})
export const ConstructionAdminSubmittals = new SharePointParentFolderItem({
  id: 'bb6f1050-2e2e-4ea6-a926-48fac9cdf4ee',
  module: CAS,
  name: 'Construction Admin Submittals',
  description: 'package number and package name joined with a hyphen',
  classType: 'ConstructionAdminSubmittal',
  primaryKey: 'id',
  folderKey: 'sharePointFolderName',
})
export const DesignSubmittals = new SharePointParentFolderItem({
  id: '44705892-6044-4cc2-ae33-2f83b3a8445b',
  module: DS,
  name: 'Design Submittals',
  description: 'package number and package name joined with a hyphen',
  classType: 'DesignSubmittal',
  primaryKey: 'id',
  folderKey: 'sharePointFolderName',
})
export const FieldDesignChanges = new SharePointParentFolderItem({
  id: '91811caf-b014-4e73-8c06-df0b760677fb',
  module: FDC,
  name: 'Field Design Changes',
  description: 'number',
  classType: 'FieldDesignChange',
  primaryKey: 'id',
  folderKey: 'number',
})
export const RequestForInformation = new SharePointParentFolderItem({
  id: 'bd7c6c81-55dc-45f6-9d04-14969fcc2e72',
  module: RFI,
  name: 'Requests For Information',
  description: 'number',
  classType: 'RequestForInformation',
  primaryKey: 'id',
  folderKey: 'number',
})
export const Issues = new SharePointParentFolderItem({
  id: 'c045bc40-f144-4fa5-b4a7-7be4626d4619',
  module: Is,
  name: 'Issues',
  description: 'number',
  classType: 'Issue',
  primaryKey: 'id',
  folderKey: 'number',
})
